import React, { useState, useEffect } from 'react'

import { Avatar, Modal } from 'antd';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { removeFalsyKeys, calcIndexByOrder } from 'utils/common';
import { useCustomSearchParams } from 'hooks/useCustomSearchParams';
import { PATH_NAME_ACTION } from 'constants/routes';
import { formatTime } from 'utils/timeUtils';

import { useMenus, usePatchOdersMenu, useDeleteMenu } from 'api/menuApi';

import { StyledActions, StyledButton } from 'styles/overrides';
import { Table } from 'components/table';
import { Publish } from 'components/common';

import { MenuFilterSearch } from '../MenuFilterSearch';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

import { calcCurrentPageWithNum } from 'utils';
import { Notification } from 'components/Notification';

const Row = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props['data-row-key'],
  });

  const style = {
    ...props.style,
    transform: CSS.Transform?.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    cursor: 'move',
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
};

export default function MenuTable() {
  const intl = useIntl();
  const [search, setSearch] = useCustomSearchParams();
  const [dataSource, setDataSource] = useState([]);
  const { data: menu, isLoading, refetch } = useMenus({ params: search });
  const patchOdersMenu = usePatchOdersMenu();
  const deleteMenuMutation = useDeleteMenu();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    if (menu?.data?.length > 0) {
      setDataSource(menu?.data)
    }
  }, [menu])

  const columns = [
    {
      title: intl.formatMessage({ id: 'common.table.no' }),
      render: (text, record, index) => {
        return calcIndexByOrder({
          index,
          page: search.page,
          limit: search.page_size,
        });
      },
    },
    {
      title: intl.formatMessage({ id: 'common.table.avatar' }),
      dataIndex: 'file',
      render: (value) => <Avatar src={value?.path} size={32} />,
    },
    {
      title: intl.formatMessage({ id: 'common.table.published' }),
      dataIndex: 'status',
      render: (value) => <Publish published={value} />,
    },
    {
      title: intl.formatMessage({ id: 'common.table.createdAt' }),
      dataIndex: 'created_at',
      render: (value) => formatTime(value),
    },
    {
      render: ({ id }) => (
        <Link to={`${id}`}>
          <StyledButton type="primary">
            {intl.formatMessage({ id: 'common.btn.seeDetails' })}
          </StyledButton>
        </Link>
      ),
    },
  ];

  const handleChangeTable = ({ current: page, pageSize: limit }) => {
    setSearch(removeFalsyKeys({ ...search, page, page_size: limit }));
  };
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
  );

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      const order = dataSource?.map(item => item?.order);

      const activeIndex = dataSource.findIndex((i) => i.id === active.id);
      const overIndex = dataSource.findIndex((i) => i.id === over?.id);
      const listData = dataSource?.map(item => item);

      const result = arrayMove(listData, activeIndex, overIndex);

      setDataSource(result)
      handleUpdateTable(result, order)
    }
  };

  const handleUpdateTable = (data, order) => {

    const id = data?.map(item => item?.id);

    const req = {
      order,
      id
    }

    patchOdersMenu.mutate(req, {
      onSuccess(res) {
        refetch()
      },
      onError(err) {
        console.log("UPDATE FAIL!", err);
      }
    });
  };

  const handleDelete = () => {
    if (selectedRowKeys.length <= 0) return;

    Modal.confirm({
      title: intl.formatMessage({ id: 'common.modal.deleteTitle' }),
      okText: intl.formatMessage({ id: 'common.btn.confirm' }),
      onOk() {
        return deleteMenuMutation.mutateAsync(selectedRowKeys, {
          onSuccess() {
            const calculatedPage = calcCurrentPageWithNum({
              page: search.page,
              limit: search.page_size,
              total: menu?.meta?.total,
              num: selectedRowKeys?.length,
            });
            if (calculatedPage !== search.page) {
              setSearch(
                removeFalsyKeys({
                  ...search,
                  page: calculatedPage,
                }),
              );
            } else {
              refetch();
            }

            setSelectedRowKeys([]);
            Notification(
              'success',
              intl.formatMessage({ id: 'message.deleteSuccess' }),
            );
          },
          onError(error) {
            Notification(
              'error',
              error?.message ||
              intl.formatMessage({ id: 'message.commonError' }),
            );
          },
        });
      },
      onCancel() { },
    });
  };

  return (
    <>
      <MenuFilterSearch
        onSubmit={(values) => setSearch(values)}
        initialValues={search}
      />
      <StyledActions size={24}>
        <Link to={PATH_NAME_ACTION.CREATE}>
          <StyledButton type="primary">
            {intl.formatMessage({ id: 'common.btn.addNew' })}
          </StyledButton>
        </Link>

        <StyledButton
          onClick={handleDelete}
          disabled={selectedRowKeys.length === 0}
        >
          {intl.formatMessage({ id: 'common.btn.delete' })}
        </StyledButton>
      </StyledActions>
      <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          items={dataSource?.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            rowKey="id"
            components={{
              body: {
                row: Row,
              },
            }}
            rowSelection={{
              selectedRowKeys,
              onChange: (newSelectedRowKeys, newSelectedRows) => {
                setSelectedRowKeys(newSelectedRowKeys);
              },
              preserveSelectedRowKeys: true,
            }}
            columns={columns}
            dataSource={menu?.data?.length > 0 ? dataSource : []}
            loading={isLoading}
            pagination={false}
            onChange={handleChangeTable}
          />
        </SortableContext>
      </DndContext>
    </>
  )
}